import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  EditShipmentDocumentInput,
  EditShipmentNoteInput,
  GetShipmentQuery,
  InvoicingProcess,
  ShipmentEventType,
  ShipmentNoteInput,
  Status,
  TransactionInput,
  useAddShipmentDocumentsMutation,
  useAddShipmentEventMutation,
  useAddShipmentNotesMutation,
  useDeleteDocumentShipmentMutation,
  useDeleteShipmentMutation,
  useDeleteShipmentNoteMutation,
  useEditDocumentShipmentMutation,
  useEditInvoiceMutation,
  useEditShipmentNoteMutation,
  useGetOrganizationSettingsQuery,
  useGetShipmentQuery,
  useGetTripQuery,
  useLockOrderMutation,
  useRecalculateInvoiceChargesMutation,
  useResendDocumentsToReceiversMutation,
  useRestoreShipmentMutation,
  useUpdateShipmentMutation,
} from "../../../graphql/generated";
import { showToast } from "../../../redux/slices/alert/Alert.slice";
import {
  ShipmentChargeInputData,
  ShipmentDocumentInputData,
} from "../../../redux/slices/Types";
import ShipmentInfos, {
  IN_REVIEW_POLL_INTERVAL_MS,
  IN_REVIEW_POLL_MAX_MS,
  ShipmentInfosProps,
  shouldFetchShipmentDuringInReviewPoll,
} from "./ShipmentInfos";
import { omit } from "lodash";
import { useSnackbar } from "notistack";
import useDialog from "../../../utils/hooks/useDialog";
import { useTranslation } from "react-i18next";
import { useConfirm } from "material-ui-confirm";
import { useShipmentInvoice } from "../../../hooks/useShipmentInvoice";
import { queryClient } from "../../../providers/QueryClient";

export type ShipmentInfo = GetShipmentQuery["shipmentById"];

type ShipmentInfosContainerProps = Omit<
  ShipmentInfosProps,
  | "onDelete"
  | "onRestore"
  | "loading"
  | "uploadingDocuments"
  | "error"
  | "shipment"
  | "trip"
  | "onDocumentsUploaded"
  | "onNotesAdded"
  | "onNoteEdited"
  | "onNoteDeleted"
  | "onDocumentDeleted"
  | "onDocumentsChanged"
  | "onChargeAdded"
  | "onChargesChanged"
  | "onChargeDeleted"
  | "onExpenseAdded"
  | "onExpensesChanged"
  | "onExpenseDeleted"
  | "onRefresh"
  | "onRecalculate"
  | "recalculating"
  | "canInvoice"
  | "onShipmentLock"
  | "onShipmentUnlock"
  | "onAccept"
  | "onResendDocuments"
  | "resendingDocuments"
> & {
  onLoad?: (shipment: ShipmentInfo) => void;
  onDeleted?: (shipmentId: string) => void;
  onRefresh?: () => void | Promise<void>;
  shipmentId: string;
};

const ShipmentInfosContainer = ({
  onLoad,
  onDeleted,
  onRefresh: onRefreshProp,
  shipmentId,
  ...props
}: ShipmentInfosContainerProps) => {
  const { t } = useTranslation(["orders", "common"]);
  const { data, isLoading, error, refetch } = useGetShipmentQuery({
    id: shipmentId,
  });
  const shipment = data?.shipmentById;

  useEffect(() => {
    if (!shipmentId) {
      return;
    }

    const queryKey = useGetShipmentQuery.getKey({ id: shipmentId });
    const startedAt = Date.now();

    const poll = () => {
      if (Date.now() - startedAt >= IN_REVIEW_POLL_MAX_MS) {
        return;
      }
      const cached = queryClient.getQueryData<GetShipmentQuery>(queryKey);
      if (!shouldFetchShipmentDuringInReviewPoll(cached?.shipmentById?.status)) {
        return;
      }
      void queryClient.fetchQuery({
        queryKey,
        queryFn: useGetShipmentQuery.fetcher({ id: shipmentId }),
        staleTime: 0,
      });
    };

    const intervalId = window.setInterval(poll, IN_REVIEW_POLL_INTERVAL_MS);
    const immediateId = window.setTimeout(poll, 400);
    const stopId = window.setTimeout(() => {
      window.clearInterval(intervalId);
    }, IN_REVIEW_POLL_MAX_MS);

    return () => {
      window.clearInterval(intervalId);
      window.clearTimeout(immediateId);
      window.clearTimeout(stopId);
    };
  }, [shipmentId]);
  const getTripDetails = useGetTripQuery({
    id: data?.shipmentById?.tripId || "",
  });
  const updateShipmentMutation = useUpdateShipmentMutation();
  const lockOrderMutation = useLockOrderMutation();
  const deleteShipmentMutation = useDeleteShipmentMutation();
  const restoreShipmentMutation = useRestoreShipmentMutation();
  const [isUploading, setIsUploading] = useState(false);
  const [isResendingDocuments, setIsResendingDocuments] = useState(false);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { showDialog, hideDialog } = useDialog();
  const { mutateAsync: addShipmentDocuments } = useAddShipmentDocumentsMutation(
    {}
  );
  const { mutateAsync: addShipmentNotes } = useAddShipmentNotesMutation({});
  const { mutateAsync: addShipmentEvent } = useAddShipmentEventMutation({});
  const { mutateAsync: editNoteShipment } = useEditShipmentNoteMutation({});
  const { mutateAsync: deleteShipmentNote } = useDeleteShipmentNoteMutation({});
  const { mutateAsync: deleteDocumentShipment } =
    useDeleteDocumentShipmentMutation({});
  const { mutateAsync: editDocumentShipment } = useEditDocumentShipmentMutation(
    {}
  );
  const { mutateAsync: resendDocumentsToReceivers } =
    useResendDocumentsToReceiversMutation({});
  const confirm = useConfirm();

  const getOrgSettingsQuery = useGetOrganizationSettingsQuery();

  // Fetch invoice for this shipment
  const {
    invoice,
    loading: invoiceLoading,
    error: invoiceError,
    refetch: refetchInvoice,
  } = useShipmentInvoice(shipmentId);

  // Mutation to update invoice charges
  const editInvoiceMutation = useEditInvoiceMutation();
  const recalculateInvoiceMutation = useRecalculateInvoiceChargesMutation();

  useEffect(() => {
    if (data?.shipmentById) {
      onLoad?.(data?.shipmentById);
    }
  }, [data?.shipmentById, onLoad]);

  const handleDocumentsUploaded = async (
    documents: Array<ShipmentDocumentInputData>,
    action: "add" | "edit" = "add"
  ) => {
    if (!documents.length) {
      return null;
    }
    setIsUploading(true);
    try {
      await Promise.all(
        documents.map(async (d) => {
          if (!d.url) {
            await fetch(d.url, {
              method: "PUT",
              body: d.file,
            });
          }
        })
      );
      if (action === "add") {
        await addShipmentDocuments({
          data: documents.map((d) => omit(d, ["file", "_id"])),
          shipment: shipment?._id || "",
        });
      } else {
        const data: EditShipmentDocumentInput = {
          _id: documents[0]._id,
          name: documents[0].name,
          type: documents[0].type,
          url: documents[0].url,
          isBillable: documents[0].isBillable,
          isForRatecons: documents[0].isForRatecons,
          isForReceivers: documents[0].isForReceivers,
          accessLevel: documents[0].accessLevel,
          associatedCharge: documents[0].associatedCharge,
          shipper: documents[0].shipper,
          receiver: documents[0].receiver,
          shipment: shipment?._id || "",
        };
        await editDocumentShipment({
          data,
        });
      }
      enqueueSnackbar(
        t(
          "common:documents.filesUploadSuccess",
          "File(s) successfully uploaded"
        )
      );
      refetch();
    } catch (error) {
      showDialog({
        type: "error",
        title: t(
          "common:documents.errors.uploadDocumentsError",
          "Error while uploading document(s)"
        ),
        description:
          (error as Error).message ||
          t("common:error.unknownError", "Unknown error"),
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleNotesAdded = async (notes: Array<ShipmentNoteInput>) => {
    try {
      await addShipmentNotes({
        data: {
          notes: notes.map((note) => omit(note, "_id")),
          shipment: shipment?._id || "",
        },
      });
      enqueueSnackbar(t("notes.addNotesSuccess", "Note(s) successfully added"));
      refetch();
    } catch (error) {
      showDialog({
        type: "error",
        title: t("orders:error.addNotesError", "Error while adding note(s)"),
        description:
          (error as Error).message ||
          t("common:error.unknownError", "Unknown error"),
      });
    }
  };

  const handleNoteEdited = async (note: EditShipmentNoteInput) => {
    try {
      await editNoteShipment({
        data: { ...note, shipment: shipment?._id },
      });
      enqueueSnackbar(t("notes.updateSuccess", "Note updated"));
      refetch();
    } catch (error) {
      showDialog({
        type: "error",
        title: t(
          "error.updateNoteError",
          "An error occurred while updating note"
        ),
        description:
          (error as Error).message ||
          t("common:error.unknownError", "Unknown error"),
      });
    }
  };

  const handleNoteDeleted = async (id: string) => {
    try {
      await deleteShipmentNote({
        data: { _id: id, shipment: shipment?._id || "" },
      });
      enqueueSnackbar(
        t("notes.deleteNoteSuccess", "Note successfully deleted")
      );
      refetch();
    } catch (error) {
      showDialog({
        type: "error",
        title: t(
          "error.deleteNoteError",
          "An error occurred while deleting note"
        ),
        description:
          (error as Error).message ||
          t("common:error.unknownError", "Unknown error"),
      });
    }
  };

  const handleDocumentDeleted = async (id: string) => {
    try {
      await deleteDocumentShipment({
        data: { _id: id, shipment: shipment?._id || "" },
      });
      enqueueSnackbar("Document successfully deleted");
      refetch();
    } catch (error) {
      showDialog({
        type: "error",
        title: t(
          "common:documents.errors.deleteError",
          "An error occurred while deleting document"
        ),
        description:
          (error as Error).message ||
          t("common:error.unknownError", "Unknown error"),
      });
    }
  };

  const handleDocumentsChanged = async (
    documents: ShipmentDocumentInputData[]
  ) => {
    try {
      if (!shipment) {
        return;
      }
      await updateShipmentMutation.mutateAsync({
        id: shipment._id,
        editShipmentData: {
          documents: documents.map((document) => omit(document, "_id", "file")),
        },
      });
      enqueueSnackbar(
        t("common:documents.updateSuccess", "Document successfully edited")
      );
      refetch();
    } catch (error) {
      showDialog({
        type: "error",
        title: t(
          "common:documents.errors.updateDocumentError",
          "An error occurred while editing the document"
        ),
        description:
          (error as Error).message ||
          t("common:error.unknownError", "Unknown error"),
      });
    }
  };

  const handleChargeAdded = async (charge: ShipmentChargeInputData) => {
    try {
      if (!invoice) {
        showDialog({
          type: "error",
          title: t(
            "error.addChargeError",
            "An error occurred while adding the charge"
          ),
          description: t("error.noInvoiceFound", "No invoice found for shipment"),
        });
        return;
      }

      const updatedCharges = [
        ...invoice.charges.map((c) => ({
          _id: c._id,
          label: c.label,
          rate: c.rate,
          unit: c.unit,
          unitType: c.unitType,
          reimburseToDriver: c.reimburseToDriver,
          taxable: c.taxable,
          chargeType: c.chargeType,
          shipmentId: c.shipmentId || shipmentId,
          billingRule: c.billingRule?._id,
          ratingTable: c.ratingTable?._id,
          document: c.document,
          relatedTransactionId: c.relatedTransactionId,
        })),
        {
          ...charge,
          shipmentId: shipmentId,
        },
      ];

      await editInvoiceMutation.mutateAsync({
        id: invoice._id,
        editInvoiceData: {
          charges: updatedCharges,
        },
      });

      enqueueSnackbar(t("addChargeSuccess", "Charge successfully added"));
      await refetchInvoice();
      refetch();
    } catch (error) {
      showDialog({
        type: "error",
        title: t(
          "error.addChargeError",
          "An error occurred while adding the charge"
        ),
        description:
          (error as Error).message ||
          t("common:error.unknownError", "Unknown error"),
      });
    }
  };

  const handleChargeDeleted = async (id: string) => {
    try {
      if (!invoice) {
        showDialog({
          type: "error",
          title: t(
            "error.deleteChargeError",
            "An error occurred while deleting charge"
          ),
          description: t("error.noInvoiceFound", "No invoice found for shipment"),
        });
        return;
      }

      const updatedCharges = invoice.charges
        .filter((c) => c._id !== id)
        .map((c) => ({
          _id: c._id,
          label: c.label,
          rate: c.rate,
          unit: c.unit,
          unitType: c.unitType,
          reimburseToDriver: c.reimburseToDriver,
          taxable: c.taxable,
          chargeType: c.chargeType,
          shipmentId: c.shipmentId || shipmentId,
          billingRule: c.billingRule?._id,
          ratingTable: c.ratingTable?._id,
          document: c.document,
          relatedTransactionId: c.relatedTransactionId,
        }));

      await editInvoiceMutation.mutateAsync({
        id: invoice._id,
        editInvoiceData: {
          charges: updatedCharges,
        },
      });

      enqueueSnackbar(t("deleteChargeSuccess", "Charge successfully deleted"));
      await refetchInvoice();
      refetch();
    } catch (error) {
      showDialog({
        type: "error",
        title: t(
          "error.deleteChargeError",
          "An error occurred while deleting charge"
        ),
        description:
          (error as Error).message ||
          t("common:error.unknownError", "Unknown error"),
      });
    }
  };

  const handleChargesChanged = async (charges: ShipmentChargeInputData[]) => {
    try {
      if (!invoice) {
        showDialog({
          type: "error",
          title: t(
            "error.updateChargeError",
            "An error occurred while editing the charge"
          ),
          description: t("error.noInvoiceFound", "No invoice found for shipment"),
        });
        return;
      }

      const updatedCharges = charges.map((c) => ({
        _id: c._id,
        label: c.label,
        rate: c.rate,
        unit: c.unit,
        unitType: c.unitType,
        reimburseToDriver: c.reimburseToDriver,
        taxable: c.taxable,
        chargeType: c.chargeType,
        shipmentId: c.shipmentId || shipmentId,
        billingRule: c.billingRule,
        ratingTable: c.ratingTable,
        document: c.document,
        relatedTransactionId: c.relatedTransactionId,
      }));

      await editInvoiceMutation.mutateAsync({
        id: invoice._id,
        editInvoiceData: {
          charges: updatedCharges,
        },
      });

      enqueueSnackbar(t("updateChargeSuccess", "Charge successfully edited"));
      await refetchInvoice();
      refetch();
    } catch (error) {
      showDialog({
        type: "error",
        title: t(
          "error.updateChargeError",
          "An error occurred while editing the charge"
        ),
        description:
          (error as Error).message ||
          t("common:error.unknownError", "Unknown error"),
      });
    }
  };

  const handleRecalculate = async () => {
    if (!invoice) {
      return;
    }

    try {
      await recalculateInvoiceMutation.mutateAsync({
        invoiceId: invoice._id,
      });
      enqueueSnackbar(
        t(
          "invoiceRecalculateSuccess",
          "Charges recalculated successfully"
        ),
        { variant: "success" }
      );
      await refetchInvoice();
      refetch();
    } catch (error) {
      showDialog({
        type: "error",
        title: t(
          "error.recalculateChargeError",
          "An error occurred while recalculating charges"
        ),
        description:
          (error as Error).message ||
          t("common:error.unknownError", "Unknown error"),
      });
    }
  };

  const handleExpenseAdded = async (expense: TransactionInput) => {
    try {
      if (!shipment) {
        return;
      }
      await updateShipmentMutation.mutateAsync({
        id: shipment._id,
        editShipmentData: {
          // @ts-ignore
          expenses: shipment.expenses?.concat(expense),
        },
      });
      enqueueSnackbar(t("addExpenseSuccess", "Expense successfully added"));
      refetch();
    } catch (error) {
      showDialog({
        type: "error",
        title: t(
          "error.addExpenseError",
          "An error occurred while adding the expense"
        ),
        description:
          (error as Error).message ||
          t("common:error.unknownError", "Unknown error"),
      });
    }
  };

  const handleExpenseDeleted = async (id: string) => {
    try {
      if (!shipment) {
        return;
      }

      // Update shipment to remove the expense
      await updateShipmentMutation.mutateAsync({
        id: shipment._id,
        editShipmentData: {
          expenses: shipment.expenses?.filter((expense) => expense._id !== id),
        },
      });

      // If there's an invoice, remove related charges
      if (invoice) {
        const updatedCharges = invoice.charges
          .filter((charge) => charge.relatedTransactionId !== id)
          .map((charge) => ({
            _id: charge._id,
            label: charge.label,
            rate: charge.rate,
            unit: charge.unit,
            unitType: charge.unitType,
            reimburseToDriver: charge.reimburseToDriver,
            taxable: charge.taxable,
            chargeType: charge.chargeType,
            shipmentId: charge.shipmentId || shipmentId,
            billingRule: charge.billingRule?._id,
            ratingTable: charge.ratingTable?._id,
            document: charge.document,
            relatedTransactionId: charge.relatedTransactionId,
          }));

        await editInvoiceMutation.mutateAsync({
          id: invoice._id,
          editInvoiceData: {
            charges: updatedCharges,
          },
        });

        await refetchInvoice();
      }

      enqueueSnackbar(
        t("deleteExpenseSuccess", "Expense successfully deleted")
      );
      refetch();
    } catch (error) {
      showDialog({
        type: "error",
        title: t(
          "error.deleteExpenseError",
          "An error occurred while deleting expense"
        ),
        description:
          (error as Error).message ||
          t("common:error.unknownError", "Unknown error"),
      });
    }
  };

  const handleResendDocuments = async (shipmentId: string) => {
    try {
      setIsResendingDocuments(true);
      await resendDocumentsToReceivers({
        shipmentId,
      });
      enqueueSnackbar(
        t("resendDocumentsSuccess", "Documents successfully resent")
      );
      refetch();
    } catch (error) {
      showDialog({
        type: "error",
        title: t(
          "error.resendDocumentsError",
          "Error while resending documents"
        ),
        description:
          (error as Error).message ||
          t("common:error.unknownError", "Unknown error"),
      });
    } finally {
      setIsResendingDocuments(false);
    }
  };

  const handleExpensesChanged = async (expenses: TransactionInput[]) => {
    try {
      if (!shipment) {
        return;
      }

      // Update shipment expenses
      await updateShipmentMutation.mutateAsync({
        id: shipment._id,
        editShipmentData: {
          expenses,
        },
      });

      // If there's an invoice, update related charges with new expense data
      if (invoice) {
        const updatedCharges = invoice.charges.map((charge) => {
          const relatedExpense = expenses.find(
            (expense) => expense._id === charge.relatedTransactionId
          );
          if (relatedExpense) {
            // Update charge with expense data
            return {
              _id: charge._id,
              label: relatedExpense.label,
              rate: relatedExpense.rate,
              unit: relatedExpense.unit,
              unitType: relatedExpense.unitType,
              reimburseToDriver: relatedExpense.reimburseToDriver,
              taxable: charge.taxable,
              chargeType: charge.chargeType,
              shipmentId: charge.shipmentId || shipmentId,
              billingRule: charge.billingRule?._id,
              ratingTable: charge.ratingTable?._id,
              document: charge.document,
              relatedTransactionId: charge.relatedTransactionId,
            };
          }
          return {
            _id: charge._id,
            label: charge.label,
            rate: charge.rate,
            unit: charge.unit,
            unitType: charge.unitType,
            reimburseToDriver: charge.reimburseToDriver,
            taxable: charge.taxable,
            chargeType: charge.chargeType,
            shipmentId: charge.shipmentId || shipmentId,
            billingRule: charge.billingRule?._id,
            ratingTable: charge.ratingTable?._id,
            document: charge.document,
            relatedTransactionId: charge.relatedTransactionId,
          };
        });

        await editInvoiceMutation.mutateAsync({
          id: invoice._id,
          editInvoiceData: {
            charges: updatedCharges,
          },
        });

        await refetchInvoice();
      }

      enqueueSnackbar(t("updateExpenseSuccess", "Expense successfully edited"));
      refetch();
    } catch (error) {
      showDialog({
        type: "error",
        title: t(
          "error.updateExpenseError",
          "An error occurred while editing the expense"
        ),
        description:
          (error as Error).message ||
          t("common:error.unknownError", "Unknown error"),
      });
    }
  };

  const handleShipmentAccepted = async () => {
    try {
      if (!shipment) {
        return;
      }
      // Update shipment status to WAITING_FOR_ASSIGNMENT
      await updateShipmentMutation.mutateAsync({
        id: shipment._id,
        editShipmentData: {
          status: Status.WaitingForAssignment,
        },
      });

      // Log shipment accepted event
      await addShipmentEvent({
        shipmentEventData: {
          shipment: shipment._id,
          type: ShipmentEventType.ShipmentAccepted,
          trip: "",
          location: {
            latitude: 0,
            longitude: 0,
          },
        },
      });

      enqueueSnackbar(
        t("acceptShipmentSuccess", "Shipment successfully accepted")
      );
      refetch();
    } catch (error) {
      showDialog({
        type: "error",
        title: t("error.acceptShipmentError", "Error while accepting shipment"),
        description:
          (error as Error).message ||
          t("common:error.unknownError", "Unknown error"),
      });
    }
  };

  return (
    <ShipmentInfos
      shipment={shipment}
      trip={getTripDetails.data?.tripById}
      loading={isLoading}
      uploadingDocuments={isUploading}
      resendingDocuments={isResendingDocuments}
      error={error}
      invoice={invoice}
      invoiceLoading={invoiceLoading}
      invoiceError={invoiceError}
      refetchInvoice={refetchInvoice}
      canInvoice={getOrgSettingsQuery.data?.organizationSettings?.invoicing?.processes?.includes(
        InvoicingProcess.LoadBased
      )}
      onDocumentsUploaded={handleDocumentsUploaded}
      onNotesAdded={handleNotesAdded}
      onNoteEdited={handleNoteEdited}
      onNoteDeleted={handleNoteDeleted}
      onDocumentDeleted={handleDocumentDeleted}
      onDocumentsChanged={handleDocumentsChanged}
      onChargeAdded={handleChargeAdded}
      onChargeDeleted={handleChargeDeleted}
      onChargesChanged={handleChargesChanged}
      onRecalculate={handleRecalculate}
      recalculating={recalculateInvoiceMutation.isLoading}
      onExpenseAdded={handleExpenseAdded}
      onExpenseDeleted={handleExpenseDeleted}
      onExpensesChanged={handleExpensesChanged}
      onResendDocuments={handleResendDocuments}
      onDelete={async (shipmentDeletionOptions) => {
        try {
          await deleteShipmentMutation.mutateAsync({
            id: shipmentId,
            deleteShipmentData: {
              reason: shipmentDeletionOptions.reason,
              isApplicableChargeOrderCancellation:
                shipmentDeletionOptions.isApplicableChargeOrderCancellation,
            },
          });
          if (onDeleted) {
            onDeleted(shipmentId);
          } else {
            navigate("/orders");
          }
        } catch (error) {
          showToast({
            type: "error",
            message: `${t(
              "cancelShipmentError",
              "An error occurred when trying to cancel this shipment"
            )}: ${
              (deleteShipmentMutation.error as Error).message ||
              t("common:error.unknownError", "Unknown error")
            }`,
          });
        }
      }}
      onRestore={() => {
        showDialog({
          title: t("common:confirm", "Confirm"),
          description: t("restoreConfirmation", {
            shipmentNumber: shipment?.shipmentNumber,
            defaultValue: `Restore load ${shipment?.shipmentNumber} ?`,
          }),
          type: "primary",
          actions: [
            {
              title: t("common:cancel", "Cancel"),
              type: "lightPrimary",
            },
            {
              title: t("yesRestore", "Yes, restore"),
              type: "primary",
              onClick: async () => {
                try {
                  hideDialog();
                  await restoreShipmentMutation.mutateAsync({
                    id: shipmentId,
                  });
                  refetch();
                } catch (error) {
                  showToast({
                    type: "error",
                    message: `${t(
                      "error.restoreError",
                      "An error occurred when trying to restore this shipment"
                    )}: ${
                      (restoreShipmentMutation.error as Error).message ||
                      t("common:error.unknownError", "Unknown error")
                    }`,
                  });
                }
              },
            },
          ],
        });
      }}
      onShipmentLock={async () => {
        try {
          await confirm({
            description:
              "Lock order ?. This will remove all forecasted orders prior to it and may cause runouts",
          });
          await lockOrderMutation.mutateAsync({
            shipmentId,
          });
          refetch();
        } catch (error) {
          if (!error) {
            return;
          }
          showDialog({
            type: "error",
            title: t("error.lockShipmentError", "Error while locking shipment"),
            description:
              (error as Error).message ||
              t("common:error.unknownError", "Unknown error"),
          });
        }
      }}
      onShipmentUnlock={async () => {
        try {
          await confirm({
            description: "Unlock order ?",
          });
          await updateShipmentMutation.mutateAsync({
            id: shipmentId,
            editShipmentData: {
              status: Status.Forecasted,
            },
          });
          refetch();
        } catch (error) {
          if (!error) {
            return;
          }
          showDialog({
            type: "error",
            title: t(
              "error.lockShipmentError",
              "Error while unlocking shipment"
            ),
            description:
              (error as Error).message ||
              t("common:error.unknownError", "Unknown error"),
          });
        }
      }}
      onAccept={handleShipmentAccepted}
      onRefresh={async () => {
        // Always refetch the shipment query
        await refetch();
        // Also call custom refresh callback if provided (e.g., to refresh planning tool)
        if (onRefreshProp) {
          await onRefreshProp();
        }
      }}
      {...props}
    />
  );
};

export default ShipmentInfosContainer;
